import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  RefreshCw,
  Wifi,
  WifiOff,
  AlertOctagon,
  Activity,
} from "react-feather";
import { Button, Card, Col, Dropdown, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  getConnectorHealthBoardAPI,
  retryTokenTestAPI,
} from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./ConnectorHealthBoard.module.scss";

// ── Helpers ──────────────────────────────────────────────────────────────────

function cardClass(status) {
  if (status === "healthy") return styles.cardHealthy;
  if (status === "down") return styles.cardDown;
  return styles.cardDegraded;
}

function badgeClass(status) {
  if (status === "healthy") return styles.badgeHealthy;
  if (status === "down") return styles.badgeDown;
  return styles.badgeDegraded;
}

function uptimeClass(status) {
  if (status === "healthy") return styles.healthy;
  if (status === "down") return styles.down;
  return styles.degraded;
}

const FILTER_DOTS = {
  all: "var(--theme-default)",
  healthy: "#198754",
  degraded: "#ff9f43",
  down: "var(--theme-secondary)",
};

const FILTER_LABELS = {
  all: "All",
  healthy: "Healthy",
  degraded: "Degraded",
  down: "Down",
};

// ── Component ────────────────────────────────────────────────────────────────

export default function ConnectorHealthBoard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getConnectorHealthBoardAPI({ refresh });
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const allCards = data?.cards || [];

  const cards = useMemo(() => {
    if (filter === "all") return allCards;
    return allCards.filter((x) => x.status === filter);
  }, [allCards, filter]);

  const summary = useMemo(
    () => ({
      total: allCards.length,
      healthy: allCards.filter((c) => c.status === "healthy").length,
      degraded: allCards.filter((c) => c.status === "degraded").length,
      down: allCards.filter((c) => c.status === "down").length,
    }),
    [allCards],
  );

  const badProviders = useMemo(() => {
    return new Set(
      allCards.filter((c) => c.status !== "healthy").map((c) => c.key),
    );
  }, [allCards]);

  const affectedFiltered = useMemo(() => {
    return (data?.affectedTenants || []).filter((a) =>
      badProviders.has(a.provider),
    );
  }, [data, badProviders]);

  const breadcrumbs = [
    { label: "Platform", path: platformUrl("dashboard") },
    { label: "Integrations" },
  ];

  if (loading && !data) {
    return (
      <div>
        <PageHeader title="Connector health" breadcrumbs={breadcrumbs} />
        <LoadingSkeleton rows={4} cols={2} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PageHeader title="Connector health" breadcrumbs={breadcrumbs} />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load health board"
          description={error.message}
          actionLabel="Retry"
          onAction={() => load(false)}
        />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Connector health"
        breadcrumbs={breadcrumbs}
        secondaryActions={[
          {
            label: "Webhook monitor",
            variant: "outline-secondary",
            onClick: () => navigate(platformUrl("integrations/webhooks")),
          },
        ]}
      />

      {/* ── Summary tiles ── */}
      <div className={styles.summaryStrip}>
        {[
          {
            key: "total",
            label: "Total",
            value: summary.total,
            accent: "var(--theme-default)",
            icon: Activity,
          },
          {
            key: "healthy",
            label: "Healthy",
            value: summary.healthy,
            accent: "#198754",
            icon: Wifi,
          },
          {
            key: "degraded",
            label: "Degraded",
            value: summary.degraded,
            accent: "#ff9f43",
            icon: AlertOctagon,
          },
          {
            key: "down",
            label: "Down",
            value: summary.down,
            accent: "var(--theme-secondary)",
            icon: WifiOff,
          },
        ].map(({ key, label, value, accent, icon: Icon }) => (
          <div
            key={key}
            className={styles.summaryTile}
            style={{ "--tile-accent": accent }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className={styles.tileLabel}>{label}</span>
              <Icon size={15} style={{ color: accent, opacity: 0.75 }} />
            </div>
            <div className={styles.tileValue}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        <button
          className={styles.refreshBtn}
          onClick={() => load(true)}
          disabled={loading}
        >
          <RefreshCw size={13} className={loading ? styles.spinning : ""} />
          {loading ? "Refreshing…" : "Refresh tests"}
        </button>

        <Dropdown>
          <Dropdown.Toggle as="button" className={styles.filterToggle}>
            <span
              className={styles.filterDot}
              style={{ "--dot-color": FILTER_DOTS[filter] }}
            />
            {FILTER_LABELS[filter]}
          </Dropdown.Toggle>
          <Dropdown.Menu
            style={{
              borderColor: "var(--recent-border)",
              borderRadius: "0.625rem",
              boxShadow: "0 4px 20px rgba(100,73,152,0.12)",
              fontSize: "0.82rem",
              minWidth: "140px",
            }}
          >
            {["all", "healthy", "degraded", "down"].map((f) => (
              <Dropdown.Item
                key={f}
                onClick={() => setFilter(f)}
                style={{
                  color:
                    filter === f
                      ? "var(--theme-default)"
                      : "var(--body-font-color)",
                  fontWeight: filter === f ? 700 : 400,
                }}
              >
                <span
                  className={styles.filterDot}
                  style={{ "--dot-color": FILTER_DOTS[f] }}
                />
                {FILTER_LABELS[f]}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        {/* live count */}
        <span
          style={{
            marginLeft: "auto",
            fontSize: "0.72rem",
            fontWeight: 700,
            letterSpacing: "0.05em",
            color: "var(--theme-default)",
            background: "rgba(100,73,152,0.1)",
            borderRadius: 999,
            padding: "3px 12px",
          }}
        >
          {cards.length} connector{cards.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* ── Connector cards grid ── */}
      <Row className="g-3 mb-4">
        {cards.map((c) => (
          <Col md={6} key={c.id}>
            <Card
              className={`h-100 ${styles.connectorCard} ${cardClass(c.status)}`}
            >
              <Card.Body style={{ padding: "1.1rem 1.25rem 1.1rem 1.5rem" }}>
                {/* Header row */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div className={styles.providerMark}>
                      {c.name?.slice(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "0.92rem",
                          fontWeight: 700,
                          color: "var(--body-font-color)",
                        }}
                      >
                        {c.name}
                      </h3>
                      <span
                        style={{
                          fontSize: "0.67rem",
                          color: "var(--chart-text-color)",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {c.key}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`${styles.statusBadge} ${badgeClass(c.status)}`}
                  >
                    {c.status}
                  </span>
                </div>

                {/* Uptime */}
                <div
                  className={`${styles.uptimeValue} ${uptimeClass(c.status)}`}
                >
                  {Number(c.uptimePct).toFixed(1)}%
                </div>
                <div
                  style={{
                    fontSize: "0.67rem",
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: "var(--chart-text-color)",
                    marginBottom: "0.75rem",
                    fontWeight: 600,
                  }}
                >
                  uptime
                </div>

                {/* Meta */}
                <div className={styles.cardMeta}>
                  Failures (24h): <strong>{c.failures24h}</strong>
                </div>
                <div className={`${styles.cardMeta} mb-3`}>
                  Affected tenants: <strong>{c.affectedTenants}</strong>
                </div>

                <Button
                  as={Link}
                  size="sm"
                  className={styles.detailBtn}
                  variant="outline-primary"
                  to={platformUrl(`integrations/${c.key}`)}
                >
                  View details →
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Affected tenants ── */}
      <Card className={`mb-4 ${styles.sectionCard}`}>
        <Card.Header>Affected tenants</Card.Header>
        <Card.Body style={{ padding: "1rem 1.25rem" }}>
          {!affectedFiltered.length ? (
            <p
              style={{
                fontSize: "0.82rem",
                color: "var(--chart-text-color)",
                margin: 0,
                padding: "0.5rem 0",
              }}
            >
              ✓ No degraded or down providers — all systems operational.
            </p>
          ) : (
            <div>
              {["google", "openai", "microsoft", "whatsapp"].map((p) => {
                const group = affectedFiltered.filter((x) => x.provider === p);
                if (!group.length) return null;
                return (
                  <div key={p} className={styles.providerGroup}>
                    <div className={styles.providerGroupLabel}>
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: "var(--theme-secondary)",
                          display: "inline-block",
                        }}
                      />
                      {p}
                    </div>
                    {group.map((a) => (
                      <div
                        key={`${a.tenantId}-${a.issue}`}
                        className={styles.tenantRow}
                      >
                        <span className={styles.tenantName}>
                          {a.tenantName}
                        </span>
                        <span className={styles.tenantIssue}>— {a.issue}</span>
                        <div className="d-flex gap-2 ms-auto flex-shrink-0">
                          <Button
                            as={Link}
                            size="sm"
                            variant="link"
                            className={styles.openTenantBtn}
                            to={platformUrl(`tenants/${a.tenantId}`)}
                          >
                            Open tenant
                          </Button>
                          <button
                            className={styles.retryBtn}
                            onClick={async () => {
                              try {
                                await retryTokenTestAPI(a.tenantId, p);
                                toast.success("Token test OK (simulated)");
                              } catch (e) {
                                toast.error(e.message);
                              }
                            }}
                          >
                            Retry token
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Incidents + Expiring tokens ── */}
      <Row className="g-3">
        <Col lg={7}>
          <Card className={`h-100 ${styles.sectionCard}`}>
            <Card.Header>Recent incidents</Card.Header>
            <Card.Body style={{ padding: "1rem 1.25rem" }}>
              <ul className={styles.timeline}>
                {(data?.incidents || []).map((inc) => (
                  <li key={inc.id} className={styles.timelineItem}>
                    <div className={styles.incidentTs}>
                      {new Date(inc.ts).toLocaleString()}
                    </div>
                    <div className={styles.incidentProvider}>
                      {inc.provider}
                    </div>
                    <div className={styles.incidentMsg}>{inc.message}</div>
                    <span
                      className={`${styles.incidentBadge} ${inc.resolved ? styles.resolved : styles.ongoing}`}
                    >
                      {inc.resolved ? "Resolved" : "Ongoing"}
                    </span>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className={`h-100 ${styles.sectionCard}`}>
            <Card.Header>Expiring tokens · &lt; 7 days</Card.Header>
            <Card.Body style={{ padding: "1rem 1.25rem" }}>
              {(data?.expiringTokens || []).length === 0 ? (
                <p
                  style={{
                    fontSize: "0.82rem",
                    color: "var(--chart-text-color)",
                    margin: 0,
                  }}
                >
                  No tokens expiring soon.
                </p>
              ) : (
                (data?.expiringTokens || []).map((t) => (
                  <div
                    key={`${t.tenantId}-${t.provider}`}
                    className={styles.tokenRow}
                  >
                    <div>
                      <div className={styles.tokenCode}>{t.tenantCode}</div>
                      <div className={styles.tokenProvider}>{t.provider}</div>
                    </div>
                    <div className={styles.tokenExpiry}>
                      {new Date(t.expiresAt).toLocaleDateString([], {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                ))
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
